import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import PostCard from '../components/post/PostCard';
import SEO from '../components/SEO'; 
import { Search, ArrowLeft, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Carousel Images
import slide1 from '../assets/carousel/01.jpg';
import slide2 from '../assets/carousel/02.jpg';
import slide3 from '../assets/carousel/03.jpg';
import slide4 from '../assets/carousel/04.jpg';
import slide5 from '../assets/carousel/05.jpg';

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryParam = searchParams.get('q') || ''; 
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'en';

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('created_at');
  const [localSearchQuery, setLocalSearchQuery] = useState(queryParam);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [visibleCount, setVisibleCount] = useState(9);

  // Sync local input with URL params
  useEffect(() => {
    setLocalSearchQuery(queryParam);
  }, [queryParam]);

  const localSlides = useMemo(() => [
    { 
      url: slide1, 
      title: t('homepage.slides.s1_title'), 
      sub: t('homepage.slides.s1_sub'),
      caption: currentLang === 'zh' ? '河畔葡萄园中的城堡酒庄' : 'A riverside chateau surrounded by golden vineyards',
      alt: currentLang === 'zh' ? '河畔葡萄园中的城堡酒庄' : 'A riverside chateau surrounded by golden vineyards'
    },
    { 
      url: slide2, 
      title: t('homepage.slides.s2_title'), 
      sub: t('homepage.slides.s2_sub'),
      caption: currentLang === 'zh' ? '阳光下的欧洲老街与露天酒馆' : 'A sun-drenched European street lined with outdoor wine cafes',
      alt: currentLang === 'zh' ? '阳光下的欧洲老街与露天酒馆' : 'A sun-drenched European street lined with outdoor wine cafes'
    },
    { 
      url: slide3, 
      title: t('homepage.slides.s3_title'), 
      sub: t('homepage.slides.s3_sub'),
      caption: currentLang === 'zh' ? '地中海露台的海景午餐' : 'Mediterranean terrace dining with a stunning sea view',
      alt: currentLang === 'zh' ? '地中海露台的海景午餐' : 'Mediterranean terrace dining with a stunning sea view'
    },
    { 
      url: slide4, 
      title: t('homepage.slides.s4_title'), 
      sub: t('homepage.slides.s4_sub'),
      caption: currentLang === 'zh' ? '日落时分色彩斑斓的港口晚宴' : 'Waterfront dining at sunset in a colorful harbor',
      alt: currentLang === 'zh' ? '日落时分色彩斑斓的港口晚宴' : 'Waterfront dining at sunset in a colorful harbor'
    },
    { 
      url: slide5, 
      title: t('homepage.slides.s5_title'), 
      sub: t('homepage.slides.s5_sub'),
      caption: currentLang === 'zh' ? '藏满酒桶与酒瓶的古朴酒窖' : 'A rustic underground wine cellar filled with barrels and bottles',
      alt: currentLang === 'zh' ? '藏满酒桶与酒瓶的古朴酒窖' : 'A rustic underground wine cellar filled with barrels and bottles'
    }
  ], [t, currentLang]);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev === localSlides.length - 1 ? 0 : prev + 1));
  }, [localSlides.length]);

  const prevSlide = () => setCurrentSlide((prev) => (prev === 0 ? localSlides.length - 1 : prev - 1));

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  useEffect(() => {
    const fetchResults = async () => {
      try {
        setLoading(true);
        const response = await fetch('/data.json');
        if (!response.ok) throw new Error('Failed to load posts');
        const allPosts = await response.json();

        const searchLower = queryParam.toLowerCase().trim();

        let filtered = allPosts.filter(post => {
          if (!searchLower) return true;
          const titleZH = (post.title?.zh || "").toLowerCase();
          const titleEN = (post.title?.en || "").toLowerCase();
          const excerptZH = (post.excerpt?.zh || "").toLowerCase();
          const excerptEN = (post.excerpt?.en || "").toLowerCase();
          const category = (post.category || "").toLowerCase();
          const entities = (post.seo?.entities || "").toLowerCase();

          return (
            titleZH.includes(searchLower) || titleEN.includes(searchLower) || 
            excerptZH.includes(searchLower) || excerptEN.includes(searchLower) ||
            category.includes(searchLower) || entities.includes(searchLower)
          );
        });

        filtered.sort((a, b) => {
          if (sortBy === 'views') return (b.view_count || 0) - (a.view_count || 0);
          return new Date(b.date || b.created_at) - new Date(a.date || a.created_at);
        });

        setPosts(filtered);
        setVisibleCount(9);
      } catch (err) { 
        console.error("Search logic error:", err); 
        setPosts([]);
      } finally { 
        setLoading(false); 
      }
    };
    fetchResults();
  }, [queryParam, sortBy]); 

  const handleLoadMore = () => {
    setVisibleCount(prev => prev + 6);
  };

  const visiblePosts = useMemo(() => 
    posts.slice(0, visibleCount),
    [posts, visibleCount]
  );

  const handleLocalSearchSubmit = (e) => {
    e.preventDefault();
    if (localSearchQuery.trim()) {
      setSearchParams({ q: localSearchQuery.trim() });
    } else {
      setSearchParams({});
    }
  };

  const handleClear = () => {
    setLocalSearchQuery('');
    setSearchParams({});
  };

  // ===== SearchResultsPage Schema =====
  const searchSchema = useMemo(() => {
    if (typeof window === 'undefined') return null;
    
    return {
      "@context": "https://schema.org",
      "@type": "SearchResultsPage",
      "name": queryParam 
        ? `${t('search_page.results_title')}: ${queryParam}`
        : t('nav.search_placeholder'),
      "description": currentLang === 'zh'
        ? `EuroWineTrip 搜索中心：查找关于“${queryParam || '欧洲葡萄酒'}”的资讯。`
        : `Search EuroWineTrip for insights on "${queryParam || 'European Wine'}".`,
      "url": `${window.location.origin}/search${queryParam ? `?q=${encodeURIComponent(queryParam)}` : ''}`,
      "mainEntity": {
        "@type": "ItemList",
        "name": t('search_page.results'),
        "numberOfItems": posts.length,
        "itemListElement": posts.slice(0, 10).map((post, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "url": `${window.location.origin}/post/${post.slug}`,
          "name": currentLang === 'zh' ? post.title.zh : post.title.en,
          "description": post.excerpt?.[currentLang] || post.excerpt?.['en'] || '',
          "image": post.cover_image || ''
        }))
      },
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": `${window.location.origin}/search?q={search_term_string}`
        },
        "query-input": "required name=search_term_string"
      }
    };
  }, [posts, queryParam, t, currentLang]);

  if (!localSlides[currentSlide]) return null;

  return (
    <main key={i18n.language} className="flex flex-col bg-cream min-h-screen overflow-x-hidden">
      <SEO 
        title={queryParam ? `${t('search_page.results_title')}: ${queryParam}` : t('nav.search_placeholder')}
        description={currentLang === 'zh' 
          ? `EuroWineTrip 搜索中心：查找关于“${queryParam || '欧洲葡萄酒'}”的资讯。` 
          : `Search EuroWineTrip for insights on "${queryParam || 'European Wine'}".`}
        slug={queryParam ? `search?q=${encodeURIComponent(queryParam)}` : 'search'}
        image={slide2}
        type="website"
        keywords={queryParam ? queryParam : '葡萄酒, 欧洲酒庄, 品鉴'}
      />

      {/* SearchResultsPage Schema */}
      {searchSchema && (
        <script type="application/ld+json">
          {JSON.stringify(searchSchema)}
        </script>
      )}

      <div className="h-20 md:h-24 w-full" />

      {/* --- HERO CAROUSEL SECTION --- */}
      <section className="relative w-screen h-[450px] md:h-[700px] bg-dark-olive shadow-2xl overflow-hidden mt-4 md:mt-8 left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] group">
        <div className="relative w-full h-full">
          <figure className="w-full h-full m-0 p-0 relative">
            <img 
              key={currentSlide}
              src={localSlides[currentSlide].url} 
              alt={localSlides[currentSlide].alt} 
              className="w-full h-full object-cover opacity-80 transition-opacity duration-1000 ease-in-out animate-fadeIn"
              loading="eager"
            />
            <figcaption className="absolute bottom-12 left-1/2 -translate-x-1/2 z-30 bg-black/40 backdrop-blur-md px-6 py-2 rounded-full text-[11px] md:text-xs text-white/80 font-serif italic tracking-wide opacity-0 group-hover:opacity-100 transition-all duration-500 border border-white/10 shadow-lg whitespace-nowrap">
              {localSlides[currentSlide].caption}
            </figcaption>
          </figure>

          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30 pointer-events-none" />
          
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center px-6 pointer-events-none">
            <h2 className="text-5xl md:text-9xl font-serif italic mb-4 drop-shadow-2xl">
              {localSlides[currentSlide].title}
            </h2>
            <p className="text-[10px] md:text-lg uppercase tracking-[0.5em] mb-10 opacity-90 font-light">
              {localSlides[currentSlide].sub}
            </p>

            <div className="flex gap-4 pointer-events-auto" role="tablist">
              {localSlides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-2 h-2 md:w-3 md:h-3 rounded-full transition-all duration-300 ${
                    currentSlide === index ? 'bg-white scale-150' : 'bg-white/30 hover:bg-white/60'
                  }`}
                  aria-label={`Slide ${index + 1}`}
                />
              ))}
            </div>
          </div>

          <button onClick={prevSlide} className="absolute left-6 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-opacity opacity-0 group-hover:opacity-100 z-40">
            <ChevronLeft size={60} strokeWidth={1} />
          </button>
          <button onClick={nextSlide} className="absolute right-6 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-opacity opacity-0 group-hover:opacity-100 z-40">
            <ChevronRight size={60} strokeWidth={1} />
          </button>
        </div>
      </section>

      <div className="container mx-auto px-4 md:px-6 mt-12">
        <Link to="/" className="flex items-center gap-2 text-olive font-bold tracking-widest text-sm mb-8 hover:text-[#800020] transition-all duration-300">
          <ArrowLeft size={18} /> {t('category_page.back_home')}
        </Link>

        <header className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 border-b border-olive/20 pb-8">
          <div className="flex flex-col">
            <h1 className="text-xl md:text-3xl font-serif text-olive italic tracking-tight">
              {queryParam ? (
                <>
                  {t('search_page.showing_results')} <span className="text-dark-olive not-italic">"{queryParam}"</span>
                </>
              ) : (
                t('nav.search_placeholder')
              )}
            </h1>
            
            {/* 搜索结果计数 - 独立一行，带换行 */}
            {queryParam && (
              <p className="text-sm text-olive/60 mt-1 font-light">
                {currentLang === 'zh' 
                  ? `找到 ${posts.length} 篇相关文章` 
                  : `Found ${posts.length} related articles`}
              </p>
            )}
          </div>

          <div className="flex flex-col md:flex-row items-center gap-6 w-full lg:w-auto">
            <div className="flex bg-olive/10 p-1.5 rounded-full border border-olive/30 shadow-sm mx-auto md:mx-0 shrink-0" role="group">
              <button 
                onClick={() => setSortBy('created_at')}
                className={`px-8 py-2.5 rounded-full text-[13px] md:text-xs font-bold tracking-[0.15em] uppercase transition-all duration-300 ${sortBy === 'created_at' ? 'bg-olive text-white shadow-md' : 'text-olive hover:bg-olive/5'}`}
                aria-pressed={sortBy === 'created_at'}
              >
                {t('homepage.recent')}
              </button>
              <button 
                onClick={() => setSortBy('views')}
                className={`px-8 py-2.5 rounded-full text-[13px] md:text-xs font-bold tracking-[0.15em] uppercase transition-all duration-300 ${sortBy === 'views' ? 'bg-olive text-white shadow-md' : 'text-olive hover:bg-olive/5'}`}
                aria-pressed={sortBy === 'views'}
              >
                {t('homepage.popular')}
              </button>
            </div>

            <form onSubmit={handleLocalSearchSubmit} className="relative w-full md:w-72" role="search">
              <label htmlFor="search-input" className="sr-only">
                {t('nav.search_placeholder')}
              </label>
              <input 
                id="search-input"
                type="search"
                placeholder={t('nav.search_placeholder')}
                value={localSearchQuery}
                onChange={(e) => setLocalSearchQuery(e.target.value)}
                className="w-full bg-white border border-olive/20 rounded-full py-2.5 px-11 text-sm text-[#3D422C] focus:outline-none focus:border-olive shadow-sm"
                aria-label={t('nav.search_placeholder')}
              />
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-olive/40" size={18} aria-hidden="true" />
              {localSearchQuery && (
                <button 
                  type="button" 
                  onClick={handleClear} 
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-olive/40 hover:text-olive transition-colors"
                  aria-label={t('search_page.clear_search')}
                >
                  <X size={16} />
                </button>
              )}
            </form>
          </div>
        </header>
      </div>

      <section className="container mx-auto px-4 md:px-6 mb-24 mt-12" aria-live="polite">
        {loading ? (
          <div className="h-64 flex justify-center items-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-olive"></div>
          </div>
        ) : visiblePosts.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-16">
              {visiblePosts.map(post => (
                <article key={post.slug} className="transition-transform duration-300 hover:translate-y-[-8px]">
                  <PostCard post={post} />
                </article>
              ))}
            </div>
            
            {/* 加载更多按钮 */}
            {!loading && posts.length > visibleCount && (
              <div className="flex justify-center mt-16">
                <button
                  onClick={handleLoadMore}
                  className="border border-olive text-olive px-10 py-3 rounded-full text-xs font-bold tracking-widest uppercase hover:bg-olive hover:text-white transition-all duration-300"
                  aria-label={t('search_page.load_more')}
                >
                  {t('search_page.load_more')}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-20 text-olive opacity-60 italic" role="status">
            <p className="text-lg mb-4">{t('search_page.no_results')}</p>
            <p className="text-sm max-w-md mx-auto">
              {currentLang === 'zh' 
                ? '尝试使用不同的关键词，或浏览我们的分类获取更多内容。' 
                : 'Try different keywords or browse our categories for more content.'}
            </p>
            <Link 
              to="/"
              className="inline-block mt-8 border border-olive text-olive px-8 py-2 rounded-full text-xs font-bold tracking-widest uppercase hover:bg-olive hover:text-white transition-all"
            >
              {t('category_page.back_home')}
            </Link>
          </div>
        )}
      </section>
    </main>
  );
}